import { Emit, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import type { IBriefingPage, IScrapAgent } from '@/shared/model/briefing-widget.model';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import BriefingCard from '@/shared/cards/briefing/briefing-card/briefing-card.vue';
import BriefingService from '@/service/briefing.service';
import { checkIsGuestAccount } from '@/util/guest-util';
import { formatDateAgo } from '@/util/date-util';
import moment, { Moment } from 'moment';
import RecapService from '@/service/recap.service';
import { isPaidUser } from '@/util/paid-user-util';
import PodcastLoading from '@/v2/components/recap/podcast-loading/podcast-loading.vue';
import PodcastSetting from '@/v2/components/recap/podcast-setting/podcast-setting.vue';
import { convertDateToYYYYMMDD } from '@/util/date-util';

const briefingStore = namespace('briefingStore');

interface PodcastData {
  id: number;
  requestId: string;
  userId: string;
  recapId: string;
  title: string;
  url: string;
  speakerId: string;
  role: string;
  loadingData: boolean;
  image: string;
  hostName: string;
  guestName: string;
  publishedAt: string;
  createdAt: string;
}

@Component({
  methods: { formatDateAgo, convertDateToYYYYMMDD },
  computed: {
    moment() {
      return moment;
    },
  },
  components: {
    'briefing-card': BriefingCard,
    PodcastLoading,
    PodcastSetting,
  },
})
export default class BriefingCardFeaturedComponent extends Vue {
  @Prop(Array) briefingPages: IBriefingPage[] | [];
  @Prop(String) swiperName: string | '';

  @Inject('briefingService')
  private briefingService: () => BriefingService;
  @Inject('recapService') private recapService: () => RecapService;

  @briefingStore.Getter
  public getRecommendationPages!: IBriefingPage[];
  @briefingStore.Getter
  public getFeaturedPages!: IBriefingPage[];
  @briefingStore.Getter
  public getBriefingPages!: IBriefingPage[];
  @briefingStore.Getter
  public getSocialPages!: IBriefingPage[];
  @briefingStore.Mutation
  public setTotalSubscribeBriefing: (payload: number) => void;
  @briefingStore.Getter
  public getTotalSubscribeBriefing: number;
  @briefingStore.Mutation
  public setIsSubscribedBriefing: (payload: boolean) => void;
  @briefingStore.Mutation
  public setBriefingPages: (payload: IBriefingPage[]) => void;
  @briefingStore.Mutation
  public setFeaturedPages: (payload: IBriefingPage[]) => void;
  @briefingStore.Mutation
  public setSocialPages: (payload: IBriefingPage[]) => void;

  isPaid = false;
  userKeyInput = '';
  localStorageUser = JSON.parse(localStorage.getItem('localStorageUser')) || null;
  recapData: any[] = [];
  isLoading = true;
  currentPodcast: PodcastData | null = null;
  recapId = '';
  recapTitle = '';
  recapLang = '';
  openPodcast = false;
  shippingMoment: Moment | null = null;
  shippingTime = '';
  timezoneOffset = '';
  startCallAPI = false;
  checkShowPodcast = false;
  isPast = false;
  showPodcastDropdown = false;
  closingFromSettings = false;
  isClickFromSettingButton = false;
  dsConnectome = JSON.parse(localStorage.getItem('ds-connectome')) || null;
  audio: HTMLAudioElement = new Audio('');
  totalTime = 0;

  public get userIdentity(): any {
    return this.$store.getters?.account
      ? this.$store.getters?.account
      : this.$store.getters?.userIdentity
      ? this.$store.getters?.userIdentity
      : JSON.parse(localStorage.getItem('localStorageUser'))
      ? JSON.parse(localStorage.getItem('localStorageUser'))
      : null;
  }

  data() {
    return {
      swiperOptions: {
        slidesPerView: this.$route.path === '/my-briefing' ? 1 : 2,
        spaceBetween: 10,
        pagination: {
          el: '#pag-num',
          type: 'fraction',
          renderFraction(currentClass, totalClass) {
            return `<strong><span class="${currentClass}"></span></strong> / <span class="${totalClass}"></span>`;
          },
          clickable: true,
        },
        navigation: {
          nextEl: '.go-carousel-nav-next',
          prevEl: '.go-carousel-nav-prev',
        },
        loop: false,
        controller: {
          control: null,
          by: 'slide',
        },
      },
      mainSwiper: null,
    };
  }

  async mounted() {
    this.isPaid = isPaidUser();

    this.timezoneOffset = this.dsConnectome?.user.timeZone;

    await this.getDateHasData();
    await this.getRecapSettingByUserId();

    this.isPast = this.isPastShippingTime(this.timezoneOffset, this.shippingMoment);
  }

  @Emit('select')
  select(item: IBriefingPage) {
    return item;
  }

  async toggleSubscribed(payload: IBriefingPage, index: number) {
    if (checkIsGuestAccount(true)) {
      return;
    }

    const toggleStatus = payload.subscribeStatus === 'SUBSCRIBE' ? 'UN_SUBSCRIBE' : 'SUBSCRIBE';
    const subscribeCount = payload.subscribeCount + (payload.subscribeStatus === 'SUBSCRIBE' ? -1 : 1);

    if (this.$route.name === 'MyBriefing-All') {
      await this.briefingService()
        .updateTypeBriefingPage(payload.bpId, 'BRIEFING_PAGE')
        .then(res => {
          const featuredStore = this.getRecommendationPages.findIndex(e => e.bpId === payload.bpId);
          if (featuredStore >= 0) {
            this.getRecommendationPages.splice(featuredStore, 1);
          }

          const lastPinnedIndex = this.getBriefingPages.reduce((last, item, idx) => (item.isPinned ? idx : last), -1);
          const insertIndex = lastPinnedIndex + 1;
          this.getBriefingPages.splice(insertIndex, 0, {
            ...payload,
            bpType: 'MY_BRIEFING',
          });

          const sorted = [...this.getBriefingPages].sort((a, b) => {
            return new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime();
          });

          this.setBriefingPages(sorted);
        });
    } else {
      await this.briefingService()
        .updateSubscribeBriefingPage(payload.bpId, toggleStatus)
        .then(response => {
          switch (toggleStatus) {
            case 'SUBSCRIBE': {
              const featuredStore = this.getFeaturedPages.findIndex(e => e.bpId === payload.bpId);
              if (featuredStore >= 0) {
                this.getFeaturedPages.splice(featuredStore, 1);
              }
              this.getSocialPages.unshift({
                ...payload,
                updatingSubscribe: false,
                subscribeStatus: toggleStatus,
                subscribeCount: subscribeCount,
              });
              // this.setIsSubscribedBriefing(true);
              this.setTotalSubscribeBriefing(this.getTotalSubscribeBriefing + 1);
              break;
            }
            case 'UN_SUBSCRIBE': {
              const indexStore = this.getSocialPages.findIndex(e => e.bpId === payload.bpId);
              if (indexStore >= 0) {
                this.getSocialPages.splice(indexStore, 1);
              }
              this.setIsSubscribedBriefing(false);
              break;
            }
          }
          this.setFeaturedPages(this.getFeaturedPages);
          this.setSocialPages(this.getSocialPages);
        })
        .catch(error => {
          const err = error.response.data;
          const message =
            err.errorCode === 1008
              ? this.$t('briefing.label.error-subscribe-briefing-private').toString()
              : this.$t('newLearningCenter.unknownError').toString();
          this.$bvToast.toast(message, {
            toaster: 'b-toaster-bottom-right',
            title: this.$t('briefing.label.error').toString(),
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
          });
        })
        .finally(() => {
          this.$emit('updateSubscribe');
        });
    }
  }

  onSelectUserSocial(agentKey: string) {
    const userKey = this.$store.getters?.userIdentity?.id;
    if (userKey === agentKey) {
      this.$router.push('/my-briefing');
    } else {
      this.$router.push(`/social-user/${agentKey}`);
    }
  }
  async callAPIGetRecapByDate(dateGetRecapInput: string[]) {
    try {
      this.userKeyInput = this.userIdentity?.id || this.localStorageUser?.id || null;
      if (this.userKeyInput && dateGetRecapInput?.length > 0) {
        const response = await this.recapService().callApiGetRecapByDate(this.userKeyInput.toString(), dateGetRecapInput);
        if (response?.data?.body?.listBriefingData?.length > 0) {
          this.recapData = [...this.recapData, ...response.data.body.listBriefingData];
          this.currentPodcast = null;
          await this.getNewPodcast(this.recapData[0].parentDocDetail.recap_data.recap_id);
        }
      }
    } catch (e) {
      console.error('Error fetching recap data:', e);
    }
  }

  async getDateHasData() {
    const today = moment(new Date()).format('yyyy-MM-DD');
    this.recapService()
      .getListDayViewRecap(today, 4)
      .then(res => {
        const dates = res.data.body.map(date => moment(date.dayHaveRecap).format('yyyy-MM-DD'));
        this.callAPIGetRecapByDate(dates).then(() => {
          this.isLoading = false;
        });
      });
  }
  showRecap(recapId) {
    this.$router.push({
      name: 'MyRecap',
      query: {
        recapId: recapId,
      },
    });
  }

  async togglePodcastDropdown(recapId) {
    if (this.currentPodcast && this.currentPodcast.url) {
      this.openPodcast = !this.openPodcast;
    } else {
      this.showToast(this.$t('recap.toast') as string, this.$t('recap.noti') as string, 'info');
      this.$router.push({
        name: 'MyRecap',
        query: {
          recapId: recapId,
          openPodcastSetting: 'true',
        },
      });
    }
  }

  async getNewPodcast(recapId): Promise<void> {
    try {
      const response = await this.recapService().getNewPodcast(this.userKeyInput.toString(), recapId);
      if (!response) {
        return;
      }
      this.loadAudio(response.url || '');
      this.currentPodcast = {
        id: response.id,
        requestId: response.requestId,
        userId: response.userId,
        recapId: response.recapId,
        title: response.title,
        url: response.url,
        speakerId: response.speakerId,
        role: response.role,
        loadingData: response.loadingData,
        image: response.image,
        hostName: response.hostName,
        guestName: response.guestName,
        publishedAt: response.publishedAt,
        createdAt: response.createdAt,
      };
    } catch (error) {
      console.error('Error in getNewPodcast:', error);
      this.isLoading = false;
    }
  }

  private showToast(message: string, title: string, variant = 'info'): void {
    this.$bvToast.toast(message, {
      title: title,
      variant: variant,
      solid: true,
      autoHideDelay: 5000,
      toaster: 'b-toaster-bottom-right',
      noCloseButton: false,
      appendToast: true,
    });
  }
  private async getRecapSettingByUserId(): Promise<void> {
    try {
      const response = await this.recapService().getRecapSettingByUserId(this.localStorageUser?.id);
      if (response) {
        this.shippingTime = response.shippingTime;
      } else {
        this.shippingTime = '06:00:00';
      }

      if (this.shippingTime && this.timezoneOffset) {
        const now = moment().utcOffset(this.timezoneOffset);
        const [hours, minutes, seconds] = this.shippingTime.split(':').map(Number);

        this.shippingMoment = now
          .clone()
          .set({ hour: hours, minute: minutes, second: seconds || 0 })
          .add(3, 'hours');
      }
    } catch (error) {
      console.error('Error fetching recap settings:', error);
    }
  }

  handleGenerationCompleted({ startCall = false, checkShowPodcast = false } = {}) {
    this.checkShowPodcast = checkShowPodcast;
    this.showPodcastDropdown = false;
    this.startCallAPI = startCall;
  }

  closePodcast(event: MouseEvent) {
    const podcastLoadingEl = this.$el.querySelector('.podcast-modal');
    if (podcastLoadingEl && podcastLoadingEl.contains(event.target as Node)) {
      return;
    }
    this.showPodcastDropdown = false;
  }
  handleGoBack() {
    this.isClickFromSettingButton = true;
    this.openPodcast = false;
    setTimeout(() => {
      this.showPodcastDropdown = true;
    }, 50);
  }
  closePodcastLoading() {
    if (!this.isClickFromSettingButton) {
      this.openPodcast = false;
    }
    this.isClickFromSettingButton = false;
  }

  isPastShippingTime(timezoneOffset, shippingMoment): boolean {
    if (!this.shippingTime || !timezoneOffset) return false;

    const now = moment().utcOffset(timezoneOffset);

    return now.isAfter(shippingMoment);
  }

  goToSection(recapId: string, sectionIndex: number) {
    this.$router.push({
      name: 'MyRecap',
      query: {
        recapId: recapId,
        scrollToIndex: sectionIndex.toString(),
      },
    });
  }

  get isMyAgent() {
    if (this.$route.path === '/my-briefing') {
      return true;
    }
    return false;
  }
  loadAudio(url: string): void {
    this.audio.src = url;
    this.audio.load();
    this.audio.onloadedmetadata = () => {
      this.totalTime = this.audio.duration;
    };
  }
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}
